import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { getTenants } from '@/services'
import { AppButton, EmptyState, FilterChip, HealthDot, PlanBadge, Skeleton, StatusBadge, TableSkeleton } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'
import { formatDate } from '@/lib/utils'

export function TenantsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const { data: result, isLoading } = useQuery({
    queryKey: ['tenants', search, statusFilter],
    queryFn: () => getTenants({ search: search || undefined, status: statusFilter === 'all' ? undefined : statusFilter, pageSize: 50 }),
  })

  const tenants = result?.data || []
  const total = result?.count || tenants.length
  const statusCounts = tenants.reduce<Record<string, number>>((acc, tenant) => {
    acc[tenant.subscription_status] = (acc[tenant.subscription_status] || 0) + 1
    return acc
  }, {})

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Clientes</div>
          <h1>Tenants</h1>
          <div className="subtitle">{isLoading ? <Skeleton width={260} height={14} /> : `${total} tenants en total · ${statusCounts.active || 0} activos · ${statusCounts.trialing || 0} en trial`}</div>
        </div>
        <div className="page-actions">
          <AppButton variant="secondary" icon="download">Exportar</AppButton>
          <Link to="/tenants/new"><AppButton variant="primary" icon="plus">Nuevo tenant</AppButton></Link>
        </div>
      </div>

      <div className="table-wrap">
        <div className="table-toolbar">
          <div className="table-search">
            <Icon name="search" size={14} />
            <input aria-label="Buscar tenants" placeholder="Buscar por nombre o slug…" value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>
          <FilterChip active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} label="Todos" count={isLoading ? undefined : total} />
          <FilterChip active={statusFilter === 'active'} onClick={() => setStatusFilter('active')} label="Activos" count={isLoading ? undefined : statusCounts.active || 0} />
          <FilterChip active={statusFilter === 'trialing'} onClick={() => setStatusFilter('trialing')} label="Trial" count={isLoading ? undefined : statusCounts.trialing || 0} />
          <FilterChip active={statusFilter === 'past_due'} onClick={() => setStatusFilter('past_due')} label="Past due" count={isLoading ? undefined : statusCounts.past_due || 0} />
          <FilterChip active={statusFilter === 'cancelled'} onClick={() => setStatusFilter('cancelled')} label="Cancelados" count={isLoading ? undefined : statusCounts.cancelled || 0} />
        </div>

        {isLoading ? (
          <TableSkeleton columns={8} rows={8} />
        ) : tenants.length === 0 ? (
          <EmptyState icon="building" title="No hay tenants con ese filtro" desc="Proba limpiar la busqueda o ampliar el filtro." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Plan</th>
                <th>Subscription</th>
                <th>Health</th>
                <th>Version</th>
                <th style={{ textAlign: 'right' }}>Pedidos 30d</th>
                <th>Creado</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.id} onClick={() => navigate(`/tenants/${tenant.id}`)}>
                  <td>
                    <Link to={`/tenants/${tenant.id}`} className="cell-primary cell-link" onClick={(e) => e.stopPropagation()}>{tenant.business_name}</Link>
                    <div className="cell-secondary"><span className="code-chip">{tenant.slug}</span></div>
                  </td>
                  <td><PlanBadge plan={tenant.plan_slug || tenant.plan_name} /></td>
                  <td><StatusBadge status={tenant.subscription_status} size="sm" /></td>
                  <td><HealthDot status={tenant.provision_status === 'active' ? 'ok' : tenant.provision_status === 'failed' ? 'down' : 'unknown'} /></td>
                  <td><span className="code-chip">{tenant.current_version || tenant.tenant_version || tenant.desired_version || 'n/a'}</span></td>
                  <td style={{ textAlign: 'right' }}><span className="tabular">{(tenant.current_orders_month || 0).toLocaleString('es-AR')}</span></td>
                  <td><span style={{ color: 'var(--text-3)', fontSize: 12 }}>{formatDate(tenant.created_at)}</span></td>
                  <td><Icon name="chevronRight" size={14} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tenants.length > 0 ? (
          <div className="table-footer">
            <span>Mostrando {tenants.length} de {total}</span>
            <div className="pager"><button disabled aria-label="Página anterior"><Icon name="chevronLeft" size={12} /></button><button className="active" aria-current="page">1</button><button>2</button><button>3</button><button aria-label="Página siguiente"><Icon name="chevronRight" size={12} /></button></div>
          </div>
        ) : null}
      </div>
    </>
  )
}
